/* theme/assets/fm-core.js — Formula layout shared behaviour (spec 2026-10-02 §6.2). Vanilla custom elements, no dependencies.
   Every duration and easing is the reference's (the motion baseline in docs/storefront-reference/). */
(() => {
  if (window.Fm) return;
  const qs = (s, el = document) => el.querySelector(s);
  const all = (s, el = document) => [...el.querySelectorAll(s)];
  const reduced = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const define = (name, cls) => { if (!customElements.get(name)) customElements.define(name, cls); };

  /* Overlays — cart drawer, mobile menu, ingredient modal, pop-up. Closed = [hidden]; .is-open drives the CSS
     transition; --fm-close-ms on the overlay is how long the closing transition runs before it is hidden again. */
  const openers = new WeakMap();
  function open(el, opener) {
    if (!el || el.classList.contains('is-open')) return;
    openers.set(el, opener || document.activeElement);
    el.hidden = false;
    el.getBoundingClientRect();
    el.classList.add('is-open');
    document.documentElement.classList.add('fm-locked');
    el.dispatchEvent(new CustomEvent('fm:open', { bubbles: true }));
    qs('[data-fm-autofocus]', el)?.focus({ preventScroll: true });
  }
  function close(el) {
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    const ms = reduced() ? 0 : parseFloat(getComputedStyle(el).getPropertyValue('--fm-close-ms')) || 0;
    setTimeout(() => { if (!el.classList.contains('is-open')) el.hidden = true; }, ms);
    if (!qs('.fm-overlay.is-open')) document.documentElement.classList.remove('fm-locked');
    openers.get(el)?.focus?.({ preventScroll: true });
    el.dispatchEvent(new CustomEvent('fm:close', { bubbles: true }));
  }
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-fm-open]');
    if (opener) {
      const t = document.getElementById(opener.dataset.fmOpen);
      if (t) { e.preventDefault(); open(t, opener); if (opener.dataset.fmSlide != null) t.querySelector('fm-slider')?.goToReal(Number(opener.dataset.fmSlide)); }
      return;
    }
    const closer = e.target.closest('[data-fm-close]');
    if (closer) { const t = closer.closest('.fm-overlay'); if (t) { e.preventDefault(); close(t); } return; }
    all('details.fm-dropdown[open]').forEach((d) => { if (!d.contains(e.target)) d.open = false; });
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    all('.fm-overlay.is-open').forEach(close);
    all('details.fm-dropdown[open]').forEach((d) => { d.open = false; });
  });

  /* Transform slider (Swiper-like): testimonials (loop), ingredient modal (centred; inactive slides at .9 in CSS),
     product videos. Per-view and gap come from CSS (--fm-per-view, --fm-gap) so breakpoints live in the stylesheet.
     Slide width = (width − gap × (perView − 1)) / perView, as Swiper computes it. */
  class FmSlider extends HTMLElement {
    connectedCallback() {
      if (this.track) return;
      this.track = qs('[data-fm-track]', this);
      if (!this.track) return;
      this.originals = [...this.track.children];
      this.loop = this.hasAttribute('data-loop') && this.originals.length > 1;
      this.clones = [];
      if (this.loop) {
        const clone = (el) => { const c = el.cloneNode(true); c.setAttribute('aria-hidden', 'true'); c.removeAttribute('id'); c.removeAttribute('data-shopify-editor-block'); return c; };
        const before = this.originals.map(clone), after = this.originals.map(clone);
        this.clones = [...before, ...after];
        this.track.prepend(...before);
        this.track.append(...after);
      }
      this.slides = [...this.track.children];
      this.index = this.loop ? this.originals.length : 0;
      qs('[data-fm-prev]', this)?.addEventListener('click', () => this.go(this.index - 1));
      qs('[data-fm-next]', this)?.addEventListener('click', () => this.go(this.index + 1));
      this.track.addEventListener('transitionend', (e) => { if (e.target === this.track) this.settle(); });
      this.addEventListener('pointerdown', (e) => this.drag(e));
      this.addEventListener('dragstart', (e) => e.preventDefault());
      this.ro = new ResizeObserver(() => this.layout());
      this.ro.observe(this);
      this.layout();
    }
    disconnectedCallback() { this.ro?.disconnect(); }
    get perView() { return parseFloat(getComputedStyle(this).getPropertyValue('--fm-per-view')) || 1; }
    get gap() { return parseFloat(getComputedStyle(this).getPropertyValue('--fm-gap')) || 0; }
    /* data-lock (the reference's Swiper watchOverflow): when every slide fits, no loop copies, no drag, no progress bar. */
    get locked() { return this.hasAttribute('data-lock') && this.originals.length <= this.perView; }
    layout() {
      const pv = this.perView, gap = this.gap;
      this.slideW = (this.clientWidth - gap * (pv - 1)) / pv;
      this.slides.forEach((s) => { s.style.width = `${this.slideW}px`; s.style.marginRight = `${gap}px`; s.style.flexShrink = '0'; });
      const locked = this.locked;
      this.toggleAttribute('data-locked', locked);
      this.clones.forEach((c) => { c.hidden = locked; });
      this.go(this.index, false);
    }
    offsetFor(i) {
      const centre = this.hasAttribute('data-centered') ? (this.clientWidth - this.slideW) / 2 : 0;
      return centre - i * (this.slideW + this.gap);
    }
    last() { const n = this.originals.length; return this.hasAttribute('data-centered') ? n - 1 : Math.max(0, n - Math.floor(this.perView)); }
    go(i, animate = true) {
      const n = this.originals.length;
      if (!n) return;
      if (this.locked) { this.track.style.transform = 'none'; return; }
      if (!this.loop) i = Math.max(0, Math.min(i, this.last()));
      this.index = i;
      this.track.style.transitionDuration = animate && !reduced() ? `${this.dataset.speed || 300}ms` : '0ms';
      this.track.style.transform = `translate3d(${this.offsetFor(i)}px, 0, 0)`;
      const real = this.loop ? ((i % n) + n) % n : i;
      this.slides.forEach((s, j) => s.classList.toggle('is-active', j === i));
      all('[data-fm-counter]', this).forEach((c) => { c.textContent = (c.dataset.fmCounter || '{n} of {total}').replace('{n}', String(real + 1)).replace('{total}', String(n)); });
      all('[data-fm-progress]', this).forEach((p) => { p.style.setProperty('--fm-progress', String((real + 1) / n)); });
      if (!this.loop) {
        const prev = qs('[data-fm-prev]', this), next = qs('[data-fm-next]', this);
        if (prev) prev.disabled = i <= 0;
        if (next) next.disabled = i >= this.last();
      }
      if (!animate || reduced()) this.settle();
      this.dispatchEvent(new CustomEvent('fm:slide', { detail: { index: real }, bubbles: true }));
    }
    settle() {
      if (!this.loop) return;
      const n = this.originals.length;
      if (this.index < n || this.index >= 2 * n) this.go((((this.index % n) + n) % n) + n, false);
    }
    goToReal(i) { this.go(this.loop ? i + this.originals.length : i, false); }
    /* A drag may start anywhere on a slide, a video included (video slides are covered edge to edge by the video);
       once it has moved, the click that follows is swallowed so a swipe never plays, pauses or follows a link. */
    drag(e) {
      if (this.locked || e.button !== 0 || e.target.closest('button, a, input, select')) return;
      const x0 = e.clientX, base = this.offsetFor(this.index);
      let dx = 0;
      const move = (ev) => { dx = ev.clientX - x0; this.track.style.transitionDuration = '0ms'; this.track.style.transform = `translate3d(${base + dx}px, 0, 0)`; };
      const up = () => {
        removeEventListener('pointermove', move); removeEventListener('pointerup', up); removeEventListener('pointercancel', up);
        if (Math.abs(dx) > 5) {
          const swallow = (ev) => { ev.preventDefault(); ev.stopPropagation(); };
          this.addEventListener('click', swallow, { capture: true, once: true });
          setTimeout(() => this.removeEventListener('click', swallow, { capture: true }), 0);
        }
        this.go(this.index + (dx < -40 ? 1 : dx > 40 ? -1 : 0));
      };
      addEventListener('pointermove', move); addEventListener('pointerup', up); addEventListener('pointercancel', up);
    }
  }
  define('fm-slider', FmSlider);

  /* Product gallery: a native scroll container with snap (the reference's gallery scrolls smoothly, ~530ms at 1440)
     plus a thumbnail strip; prev/next step one image. */
  class FmGallery extends HTMLElement {
    connectedCallback() {
      if (this.main) return;
      this.main = qs('[data-fm-gallery-main]', this);
      this.strip = qs('[data-fm-thumbs]', this);
      this.thumbs = all('[data-fm-thumb]', this);
      this.index = 0;
      this.thumbs.forEach((t, i) => t.addEventListener('click', () => this.go(i)));
      qs('[data-fm-prev]', this)?.addEventListener('click', () => this.go(this.index - 1));
      qs('[data-fm-next]', this)?.addEventListener('click', () => this.go(this.index + 1));
      this.main?.addEventListener('scroll', () => { clearTimeout(this.t); this.t = setTimeout(() => this.sync(), 80); }, { passive: true });
      this.mark();
    }
    get slides() { return this.main ? [...this.main.children] : []; }
    go(i) {
      const n = this.slides.length;
      if (!n) return;
      this.index = Math.max(0, Math.min(n - 1, i));
      this.scrollMain(this.slides[this.index].offsetLeft);
      this.mark();
    }
    /* Scripted, not native smooth scroll: the reference settles in 480ms at 1440 and 336ms at 390 (motion baseline). With this
       ease-out curve the last pixel lands at ~92% of the run, so 520 / 370ms; snapping is paused so no step is pulled back to a slide. */
    scrollMain(to) {
      const m = this.main;
      cancelAnimationFrame(this.raf);
      if (reduced()) { m.scrollLeft = to; return; }
      const from = m.scrollLeft, ms = matchMedia('(max-width: 749px)').matches ? 370 : 520, t0 = performance.now();
      m.style.scrollSnapType = 'none';
      const step = (now) => {
        const x = Math.min(1, (now - t0) / ms);
        m.scrollLeft = from + (to - from) * (1 - Math.pow(1 - x, 3));
        if (x < 1) this.raf = requestAnimationFrame(step); else m.style.scrollSnapType = '';
      };
      this.raf = requestAnimationFrame(step);
    }
    sync() {
      const slides = this.slides;
      if (!slides.length) return;
      const x = this.main.scrollLeft;
      this.index = slides.reduce((best, s, i) => (Math.abs(s.offsetLeft - x) < Math.abs(slides[best].offsetLeft - x) ? i : best), 0);
      this.mark();
    }
    mark() {
      this.thumbs.forEach((t, j) => t.setAttribute('aria-current', String(j === this.index)));
      const t = this.thumbs[this.index];
      if (t && this.strip) this.strip.scrollTo({ left: t.offsetLeft - (this.strip.clientWidth - t.clientWidth) / 2, behavior: reduced() ? 'auto' : 'smooth' });
      qs('[data-fm-prev]', this)?.toggleAttribute('data-dim', this.index === 0);
      qs('[data-fm-next]', this)?.toggleAttribute('data-dim', this.index >= this.slides.length - 1);
    }
  }
  define('fm-gallery', FmGallery);

  /* Accordions. <details> items (buy box): open/close timing is CSS (grid-template-rows + opacity, 200ms); closing waits
     200ms before removing [open]. data-mode="height" (FAQ): the body's height and padding-top animate 200ms ease-in-out. */
  class FmAccordion extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.addEventListener('click', (e) => {
        const head = e.target.closest('[data-fm-acc-head]');
        if (!head || head.closest('fm-accordion') !== this) return;
        e.preventDefault();
        const item = head.closest('[data-fm-acc-item]');
        this.toggle(item, !item.hasAttribute('data-open'));
      });
      all('[data-fm-acc-item][data-open]', this).forEach((item) => this.toggle(item, true, false));
    }
    toggle(item, on, animate = true) {
      if (!item) return;
      const slow = animate && !reduced();
      if (on && this.hasAttribute('data-single')) all('[data-fm-acc-item][data-open]', this).forEach((o) => { if (o !== item) this.toggle(o, false, animate); });
      qs('[data-fm-acc-head]', item)?.setAttribute('aria-expanded', String(on));
      const body = qs('[data-fm-acc-body]', item);
      if (this.dataset.mode === 'height' && body) {
        const inner = body.firstElementChild;
        const pad = parseFloat(getComputedStyle(body).getPropertyValue('--fm-open-pad')) || 0;
        if (on) {
          item.setAttribute('data-open', '');
          body.style.height = `${(inner?.offsetHeight || 0) + pad}px`;
          const done = () => { if (item.hasAttribute('data-open')) body.style.height = 'auto'; };
          if (slow) body.addEventListener('transitionend', done, { once: true }); else done();
        } else {
          body.style.height = `${body.offsetHeight}px`;
          body.getBoundingClientRect();
          item.removeAttribute('data-open');
          body.style.height = '0px';
        }
        return;
      }
      if (item.tagName === 'DETAILS') {
        if (on) { item.open = true; item.getBoundingClientRect(); item.setAttribute('data-open', ''); }
        else { item.removeAttribute('data-open'); setTimeout(() => { if (!item.hasAttribute('data-open')) item.open = false; }, slow ? 200 : 0); }
        return;
      }
      item.toggleAttribute('data-open', on);
    }
  }
  define('fm-accordion', FmAccordion);

  /* Countdown to a fixed end (spec §2 decision 4): hidden when there is no end or it has passed. data-end="midnight"
     counts to the visitor's next local midnight instead and restarts every day, as the reference does (owner, 2026-10-05). */
  class FmCountdown extends HTMLElement {
    connectedCallback() {
      this.host = this.closest('[data-fm-countdown-host]') ?? this;
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); }
    tick() {
      const end = this.dataset.end === 'midnight' ? window.FmDates.nextMidnight(Date.now()) : this.dataset.end;
      const left = window.FmDates.remaining(end, Date.now());
      if (left === null) { this.host.hidden = true; this.host.setAttribute('data-fm-ended', ''); clearInterval(this.timer); return; }
      const p = window.FmDates.parts(left);
      for (const k of ['h', 'm', 's']) all(`[data-fm-${k}]`, this).forEach((el) => { el.textContent = p[k]; });
      all('[data-fm-hms]', this).forEach((el) => { el.textContent = `${p.h}:${p.m}:${p.s}`; });
    }
  }
  define('fm-countdown', FmCountdown);

  class FmShipsBy extends HTMLElement {
    connectedCallback() {
      const d = window.FmDates.shipsBy(new Date(), { days: Number(this.dataset.days), cutoffHour: Number(this.dataset.cutoff), skipWeekends: this.dataset.weekends !== 'ship' });
      const out = qs('[data-fm-date]', this);
      if (out) out.textContent = window.FmDates.formatShipDate(d, document.documentElement.lang || 'en');
    }
  }
  define('fm-ships-by', FmShipsBy);

  /* Sticky header (reference: the header section is sticky; scrolled past its own height while going down it sits at
     top −height; going up it returns with a 150ms ease-out top transition). A countdown bar inside it, below the bar,
     shows once scrollY passes data-bar-after (reference: 400) — still visible while the header itself is tucked away. */
  class FmHeader extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;
      this.section = this.closest('.shopify-section') ?? this;
      this.section.classList.add('fm-sticky');
      this.bar = qs('[data-fm-countdown-bar]', this);
      this.last = scrollY;
      const update = () => {
        const y = scrollY;
        const head = qs('.fm-header__bar', this)?.offsetHeight || 0;
        this.section.style.setProperty('--fm-header-h', `${head}px`);
        const prev = this.section.previousElementSibling;
        const start = prev && prev.classList.contains('shopify-section') ? prev.offsetTop + prev.offsetHeight : 0;
        if (y <= start + head) this.section.classList.remove('is-hidden', 'is-animate');
        else if (y > this.last) this.section.classList.add('is-hidden');
        else if (y < this.last) { this.section.classList.add('is-animate'); this.section.classList.remove('is-hidden'); }
        if (this.bar) this.bar.hidden = this.bar.hasAttribute('data-fm-ended') || y <= Number(this.dataset.barAfter || 400);
        this.last = y;
      };
      update();
      addEventListener('scroll', update, { passive: true });
      addEventListener('resize', update, { passive: true });
    }
  }
  define('fm-header', FmHeader);

  class FmTabs extends HTMLElement {
    connectedCallback() {
      if (this.tabs) return;
      this.tabs = all('[role="tab"]', this);
      this.tabs.forEach((t, i) => t.addEventListener('click', () => this.select(i)));
      this.select(Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true')));
    }
    select(i) {
      this.tabs.forEach((t, j) => {
        t.setAttribute('aria-selected', String(j === i));
        t.tabIndex = j === i ? 0 : -1;
        const panel = document.getElementById(t.getAttribute('aria-controls'));
        if (panel) panel.hidden = j !== i;
      });
    }
  }
  define('fm-tabs', FmTabs);

  class FmRotator extends HTMLElement {
    connectedCallback() {
      const items = [...this.children];
      if (items.length < 2 || this.timer) return;
      let i = 0;
      this.timer = setInterval(() => { items[i].classList.add('is-hidden'); i = (i + 1) % items.length; items[i].classList.remove('is-hidden'); }, Math.max(2, Number(this.dataset.seconds) || 5) * 1000);
    }
    disconnectedCallback() { clearInterval(this.timer); this.timer = null; }
  }
  define('fm-rotator', FmRotator);

  /* Scroll reveal (motion contract §6.2): opacity + 1px blur, 300ms ease, on sections marked .fm-reveal. */
  /* Each item reveals as it enters the viewport (as the reference's per-element scroll trigger), not the whole section at once. */
  function initReveal(root = document) {
    const items = all('.fm-reveal .fm-reveal-item:not(.is-shown)', root);
    if (!document.body.classList.contains('fm-reveal-on') || window.Shopify?.designMode || reduced() || !('IntersectionObserver' in window)) { items.forEach((s) => s.classList.add('is-shown')); return; }
    const io = new IntersectionObserver((entries) => entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('is-shown'); io.unobserve(en.target); } }), { rootMargin: '0px 0px -5% 0px' });
    items.forEach((s) => io.observe(s));
  }

  document.addEventListener('shopify:section:load', (e) => initReveal(e.target));
  document.addEventListener('shopify:section:select', (e) => { const o = qs('.fm-overlay[data-fm-editor-open]', e.target); if (o) open(o); });
  document.addEventListener('shopify:section:deselect', (e) => all('.fm-overlay.is-open', e.target).forEach(close));
  document.addEventListener('shopify:block:select', (e) => {
    const el = e.target;
    const item = el.closest('[data-fm-acc-item]');
    item?.closest('fm-accordion')?.toggle(item, true, false);
    const overlay = el.closest('.fm-overlay');
    if (overlay) open(overlay);
    if (el.matches('[role="tab"]')) el.click();
    const slide = el.closest('[data-fm-track] > *');
    const slider = slide?.closest('fm-slider');
    if (slider?.slides) slider.go(slider.slides.indexOf(slide), false);
    // A block whose content lives in a pop-up (ingredient cards): open it at the block's slide so it can be edited in place.
    const opener = el.querySelector?.('[data-fm-open][data-fm-slide]');
    const target = opener && document.getElementById(opener.dataset.fmOpen);
    if (target) { open(target); target.querySelector('fm-slider')?.goToReal(Number(opener.dataset.fmSlide)); }
  });
  document.addEventListener('shopify:block:deselect', (e) => {
    const opener = e.target.querySelector?.('[data-fm-open][data-fm-slide]');
    const target = opener && document.getElementById(opener.dataset.fmOpen);
    if (target) close(target);
  });

  document.body.classList.add('fm-js');
  window.Fm = { qs, all, open, close, reduced, initReveal };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initReveal()); else initReveal();
})();
